import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import {
  AiQuotaConfigView,
  AiQuotaOverrideInput,
  updateCompanyAiQuota,
} from '../../../api/AI/settings';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { formatUtcReset, hoursUntilReset } from '../../../utils/aiQuota';

/**
 * CO-355 — the OPERATOR's per-company daily AI quota editor.
 *
 * 🔴 The whole design problem here is ONE thing: an empty number field must read as
 * "inherits 200", never as "zero". A blank box with no annotation reads as zero to everybody who
 * has ever filled in a form, and zero would mean "this tenant gets no AI at all" — exactly what the
 * owner decision forbids. So each field carries the platform value as its PLACEHOLDER and a live
 * line underneath that changes as you type: "Inheriting the platform default of 200" ⇄ "Overriding
 * the platform default of 200". The state is stated, not implied.
 *
 * A tri-state can only be reset if the empty state is reachable, so clearing a field is a real,
 * savable value (it sends null), not a validation error.
 */

interface Props {
  open: boolean;
  config: AiQuotaConfigView | null;
  onClose: () => void;
  onSaved: (updated: AiQuotaConfigView) => void;
}

type FieldKey = 'hrAdmin' | 'chiefManager' | 'employee';

/** '' is a first-class value here: it means "clear the override". */
type FormState = Record<FieldKey, string> & { enabled: boolean | null; note: string };

const toField = (value: number | null | undefined): string => (value === null || value === undefined ? '' : String(value));

/**
 * `Input` is an untyped `.jsx` primitive with no `.d.ts`, so TypeScript sees it as taking no props
 * and every usage elsewhere in the app lands in the repo's tsc baseline. Declaring the props this
 * file actually passes keeps it out of that baseline rather than adding to it.
 */
const TextField = Input as unknown as React.FC<{
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  description?: string;
  error?: string;
  placeholder?: string;
  type?: string;
  inputMode?: 'numeric' | 'text';
  min?: number;
  max?: number;
  maxLength?: number;
}>;

const AiQuotaDialog: React.FC<Props> = ({ open, config, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState<FormState>({ hrAdmin: '', chiefManager: '', employee: '', enabled: null, note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config) return;
    setForm({
      hrAdmin: toField(config.overrideHrAdmin),
      chiefManager: toField(config.overrideChiefManager),
      employee: toField(config.overrideEmployee),
      enabled: config.overrideEnabled,
      note: config.note ?? '',
    });
    setError(null);
  }, [config]);

  const tiers: { key: FieldKey; label: string; platform: number }[] = useMemo(() => {
    if (!config) return [];
    return [
      { key: 'hrAdmin', label: t('aiQuota.tier.hrAdmin'), platform: config.platformHrAdmin },
      { key: 'chiefManager', label: t('aiQuota.tier.chiefManager'), platform: config.platformChiefManager },
      { key: 'employee', label: t('aiQuota.tier.employee'), platform: config.platformEmployee },
    ];
  }, [config, t]);

  if (!config) return null;

  const outOfRange = (raw: string): boolean => {
    if (raw === '') return false;
    const parsed = Number(raw);
    return !Number.isInteger(parsed) || parsed < config.minLimit || parsed > config.maxLimit;
  };

  const hasInvalidField = tiers.some(tier => outOfRange(form[tier.key]));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // FULL REPLACE. An empty box is sent as an explicit null so the tier goes back to inheriting;
      // omitting it would leave a stale override in place forever.
      const input: AiQuotaOverrideInput = {
        enabled: form.enabled,
        hrAdmin: form.hrAdmin === '' ? null : Number(form.hrAdmin),
        chiefManager: form.chiefManager === '' ? null : Number(form.chiefManager),
        employee: form.employee === '' ? null : Number(form.employee),
        note: form.note.trim() === '' ? null : form.note.trim(),
      };
      const result = await updateCompanyAiQuota(config.companyId, input);
      if (result.status === 200 && result.payload) {
        onSaved(result.payload);
        onClose();
        return;
      }
      setError(result.errors?.[0]?.description || t('aiQuota.saveError'));
    } catch {
      setError(t('aiQuota.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const meteringLabel = config.platformEnabled ? t('aiQuota.on') : t('aiQuota.off');

  return (
    <Dialog open={open} onOpenChange={next => { if (!next && !saving) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('aiQuota.editTitle')}: {config.companyName}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground max-w-[60ch]">{t('aiQuota.intro')}</p>

          {config.exemptByOwnKey && (
            <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 p-3">
              <KeyRound size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <p className="text-xs leading-relaxed text-muted-foreground">{t('aiQuota.ownKeyNotice')}</p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Switch
              checked={form.enabled ?? config.platformEnabled}
              onCheckedChange={checked => setForm(prev => ({ ...prev, enabled: checked }))}
              label={t('aiQuota.metered')}
              description={t('aiQuota.meteredHint')}
            />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{t('aiQuota.meteredInherit', { state: meteringLabel })}</span>
              {form.enabled !== null && (
                <button
                  type="button"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                  onClick={() => setForm(prev => ({ ...prev, enabled: null }))}
                >
                  {t('aiQuota.useDefault')}
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 border-t border-border pt-4">
            {tiers.map(tier => {
              const raw = form[tier.key];
              const invalid = outOfRange(raw);
              return (
                <TextField
                  key={tier.key}
                  type="number"
                  inputMode="numeric"
                  min={config.minLimit}
                  max={config.maxLimit}
                  label={tier.label}
                  value={raw}
                  placeholder={String(tier.platform)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm(prev => ({ ...prev, [tier.key]: e.target.value }))}
                  error={invalid ? t('aiQuota.range', { min: config.minLimit, max: config.maxLimit }) : undefined}
                  description={raw === ''
                    ? t('aiQuota.inheriting', { value: tier.platform })
                    : t('aiQuota.overriding', { value: tier.platform })}
                />
              );
            })}
          </div>

          <TextField
            label={t('aiQuota.note')}
            value={form.note}
            maxLength={500}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm(prev => ({ ...prev, note: e.target.value }))}
            description={t('aiQuota.noteHint')}
          />

          <p className="text-xs text-muted-foreground">
            {config.usedToday > 0
              ? t('aiQuota.usedToday', { requests: config.usedToday, users: config.activeUsersToday })
              : t('aiQuota.noneToday')}
            {' '}
            {t('aiQuota.resetHint', {
              resetsAt: formatUtcReset(config.resetsAtUtc, i18n.language),
              hours: hoursUntilReset(config.resetsAtUtc),
            })}
          </p>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('Cancel')}</Button>
          <Button onClick={handleSave} disabled={saving || hasInvalidField} loading={saving}>
            {saving ? t('Saving...') : t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AiQuotaDialog;
